import { sin, TAU } from '../../sim/math';

/* ═══════════════════════════════════════════════════════════════════════════
   L'HORLOGE DU COLOSSE — source unique de vérité du combat du monde 3.

   Même dispositif que les deux boss précédents, et pour la même raison : le
   moteur n'a aucune notion de phase, la synchronisation des armes n'existe que
   parce que tous les `firstDelay` dérivent d'une origine commune et que tous
   les `every` valent exactement CYCLE.

   ── Ce que ce boss enseigne ──
   Il ne pose aucune règle nouvelle : il demande de choisir. C'est la synthèse
   des deux mondes précédents, et le seul boss du jeu dont le noyau ne soit PAS
   blindé par ses sections.

   Le Héraut apprend qu'on pilote le boss. La Navette apprend qu'il ne vous
   attend pas. Le Colosse ne rajoute rien à cette grammaire — il met le joueur
   devant quatre menaces simultanées, une barre de vie atteignable dès la
   première seconde, et le laisse décider de son propre combat :

     tout démonter    le combat le plus long, mais chaque section retirée le
                      rend plus lisible. La voie sûre, et celle qui rapporte le
                      plus de points.
     dôme d'abord     supprime la seule menace visée, mais laisse le feu croisé
                      des ailes intact pendant tout le reste.
     droit au noyau   le plus court sur le papier, sous les quatre armes du
                      début à la fin. Réservé à qui a le débit pour le tenir.

   Aucune de ces voies n'est la bonne. C'est ce qui fait du Colosse un examen
   plutôt qu'un obstacle : il mesure ce que le joueur sait de son propre build.
   ═══════════════════════════════════════════════════════════════════════════ */

export const CYCLE = 9.6;

/** Âge auquel le combat commence : télégraphe (2,4 s) puis descente jusqu'à la
 *  station, apparition vers Y = +700 et station à Y = +320 à 165 u/s. */
export const COMBAT_START = 3.8;

/* ── Découpage du cycle ─────────────────────────────────────────────────────

     0,0 ─── 6,4   BALANCEMENT
                   Le porteur oscille lentement, ±160 u. Il n'esquive rien : le
                   mouvement existe pour que son ARRÊT puisse servir de signal.
       0,6         ailes  — deux éventails statiques croisés, la géographie
       2,6         socle  — un anneau lent, le volume
       4,6         dôme   — la seule menace visée du combat

     6,4 ─── 7,2   VERROU
                   Immobilisation nette. 0,8 s sans aucune émission.
                   ⚠️ Même signal que chez le Héraut et la Navette : l'arrêt
                   annonce la salve. Trois boss, une seule convention — c'est
                   ce qui permet au joueur d'arriver ici en sachant déjà lire.

     7,2           SALVE — le dôme tire sa volée resserrée.

     7,2 ─── 7,8   La salve s'écarte.

     7,7 ─── 9,6   PUNITION — 1,9 s. Plus courte que celle des deux premiers
                   boss (2,0 s) : le monde 3 resserre la fenêtre sans jamais la
                   supprimer.

                   ⚠️ 1,9 et non 1,8. La première version était calée EXACTEMENT
                   sur le minimum du plan, et le garde-fou d'import l'a refusée :
                   9,6 − 7,8 vaut 1,7999999999999998 en flottant. L'incident est
                   plus utile que le correctif — une valeur posée sur sa propre
                   limite n'a aucune marge de conception, et la moindre retouche
                   d'une autre phase l'aurait fait passer dessous sans que le
                   nombre écrit dans le fichier change.
   ────────────────────────────────────────────────────────────────────────── */

export const PHASE_AILES = 0.6;
export const PHASE_SOCLE = 2.6;
export const PHASE_DOME = 4.6;
/** Fin du balancement : télégraphe de la salve. */
export const PHASE_VERROU = 6.4;
export const PHASE_SALVE = 7.2;
export const PHASE_PUNITION = 7.7;

export function firstDelayAt(p: number): number {
  return COMBAT_START + p;
}

/** Amplitude du balancement, en unités.
 *
 *  ⚠️ Les ailes sont boulonnées à ±210 u et le demi-terrain vaut 450 : au-delà
 *  de 240, une aile sortirait du cadre et tirerait depuis le hors-champ. Une
 *  partie n'est jamais éliminée par la marge de culling — `motionSystem` la
 *  repositionne sur son porteur puis passe — donc rien ne rattraperait
 *  l'erreur. */
export const SWAY = 160;

/**
 * Abscisse du porteur, calculée depuis le temps de combat.
 *
 * ⚠️ CALCULÉE et non intégrée, pour la même raison que chez la Navette : une
 * intégration dériverait au fil des dizaines de cycles et rendrait la
 * trajectoire dépendante de l'historique des pas de temps, ce qui ferait
 * diverger deux replays d'une même graine.
 *
 * `sin` vient de src/sim/math.ts — table committée, donc identique sur tous les
 * moteurs JS. `Math.sin` est interdit par la liste blanche ESLint, et pas par
 * pédanterie : sa précision n'est pas spécifiée par le standard, ce qui suffit
 * à casser un replay entre deux navigateurs.
 */
export function carrierX(t: number): number {
  if (t <= 0) return 0;
  const k = Math.floor(t / CYCLE);
  const phase = t - k * CYCLE;
  // Au-delà du verrou, la position est GELÉE sur celle atteinte à cet instant :
  // c'est l'immobilisation qui fait le télégraphe.
  const u = phase >= PHASE_VERROU ? PHASE_VERROU : phase;
  // Un balancement complet par cycle de balancement : le joueur voit passer une
  // oscillation entière avant chaque salve, donc il peut la prévoir.
  return SWAY * sin((u / PHASE_VERROU) * TAU);
}

/* ── Garde-fous de conception ────────────────────────────────────────────── */
if (CYCLE - PHASE_PUNITION < 1.8) {
  throw new Error(
    `Colosse : fenêtre de punition de ${(CYCLE - PHASE_PUNITION).toFixed(2)} s, ` +
      `minimum 1,80 s (PLAN.md §6.6).`,
  );
}
if (PHASE_SALVE - PHASE_VERROU < 0.5) {
  throw new Error(
    `Colosse : télégraphe de salve de ${(PHASE_SALVE - PHASE_VERROU).toFixed(2)} s, ` +
      `trop court pour être lu.`,
  );
}
